import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDropdownContext } from '../contexts/DropdownContext';
import { useTheme } from '../contexts/ThemeContext';
import { toggleFullscreen, isInFullscreen } from '../utils/fullscreenUtils';
import { Menu } from 'lucide-react';
import Header from './Header';
import ToastContainer from './ToastContainer';

interface LayoutProps {
  children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { toggleDropdown } = useDropdownContext();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Redirect to miai page by default
  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/miai');
    }
  }, [location.pathname, navigate]);

  // Fullscreen change event listeners
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(isInFullscreen());
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Keyboard shortcut for F11
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F11') {
        event.preventDefault();
        toggleFullscreen();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navigation = [
    { name: 'MIAI', href: '/miai', icon: 'TrendingUp' },
    { name: 'Analytics', href: '/analytics', icon: 'BarChart3' },
    { name: 'Portfolio', href: '/portfolio', icon: 'Briefcase' },
    { name: 'Settings', href: '/settings', icon: 'Settings' },
  ];

  const currentNav = navigation.find(nav => nav.href === location.pathname);

  return (
    <div className={`min-h-screen ${theme === 'light' ? 'bg-white' : 'bg-gray-900'}`}>
      {/* Toast Container */}
      <ToastContainer />
      
      {/* Main content */}
      <div>
        {/* Mobile header */}
        {/* <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 
        border-b border-gray-700 bg-gray-800 px-4 shadow-sm sm:gap-x-6 sm:px-6 
        lg:hidden">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-300 lg:hidden"
            onClick={() => toggleDropdown('sidebar')}
            data-dropdown
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button> */}
                 <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-700 bg-gray-800 px-4 shadow-sm sm:gap-x-6 sm:px-6 hidden">
           <button
             type="button"
             className="-m-2.5 p-2.5 text-gray-300 hidden"
             onClick={() => toggleDropdown('sidebar')}
             data-dropdown
           >
             <span className="sr-only">Open sidebar</span>
             <Menu className="h-6 w-6" aria-hidden="true" />
           </button>

          {/* Mobile Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={() => navigate('/miai')}
              className="text-lg font-semibold text-gray-100 hover:text-[#2AB7A6] transition-colors duration-200 cursor-pointer"
            >
              MIAI
            </button>
          </div>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1">
              <div className="flex w-full md:ml-0">
                <div className="relative w-full text-gray-400 focus-within:text-gray-300">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-lg font-semibold text-gray-100">
                      {currentNav?.name || 'Live Trading'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop header */}
        <Header />

        {/* Page content */}
        <main className={`h-full min-h-screen ${theme === 'light' ? 'bg-white' : 'bg-gray-900'}`}>
          <div className="w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
